import { Fragment, useMemo, useEffect, type ReactNode, type CSSProperties } from "react";
import classNames from "classnames";
import { Link, useParams, useHistory, useLocation } from "react-router-dom";
import { compile } from "path-to-regexp";
import { MediaQuery } from "@outplayed/responsive";
import { TooltipContainer } from "@outplayed/tooltips";
import { AppRoutes, getClassicChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { OVERVIEW_QUEUE_TYPE_OPTIONS } from "@ugg/shared/query-params/filter-options/champions/overview";
import { CLASSIC_PATCH_OPTION } from "@ugg/shared/query-params/valid-params";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import { ChampionProfilePageNav } from "@ugg/shared/components/Champions/ChampionProfilePageNav";
import { HeaderPatch } from "@ugg/shared/components/common/HeaderPatch";
import DefaultSelect from "@ugg/shared/components/FilterManager/DefaultSelect";
import { FilterManagerWrapper } from "@ugg/shared/components/FilterManager/FilterManager";
import { ROLE_OPTIONS, REGION_OPTIONS_ONLY_WORLD } from "@ugg/shared/query-params/filter-options/common";
import { ReactComponent as FilterIcon } from "@ugg/shared/assets/svg/filters-icon.svg";
import { PersonalizedMatchupsPlusCTA } from "@ugg/shared/components/Champions/pages/Overview/ToughestMatchups";
import { useUGGPatches } from "@ugg/shared/api/requests/patches";
import { convertPatchDisplay } from "@ugg/shared/utils/patch-helpers";
import { getRoleLabel, isSRMainRole, normalizeRole } from "@ugg/shared/utils/role-helpers";
import { ALL_TIERS, getTierGradeColorHex, type TierNameValue } from "@ugg/shared/utils/tier-helpers";
import { useClassicChampionSEO } from "@ugg/shared/utils/seo-helpers";
import { ReactComponent as Warning } from "@ugg/shared/assets/svg/warning.svg";
import { ReactComponent as ArrowRight } from "@ugg/shared/assets/svg/arrow-right.svg";
import {
  useClassicChampions,
  useClassicChampionJSON,
  useClassicChampionSkillsMini,
  useClassicMasteryDisplay,
  useClassicChampionMastery,
  useClassicChampionTierList,
  getClassicRoleLetter,
  getClassicRoleFromLetter,
  useClassicSpells,
  useClassicSpellBuild,
  useClassicPerks,
  useClassicRuneBuild,
  useClassicItems,
  useClassicItemsMini,
  useClassicItemBuild,
  getClassicChampionImgUrl,
  getClassicChampionSkillImgUrl,
  getClassicMasteryIconUrl,
  getClassicMasteryTreeBgUrl,
  getClassicSpellIconUrl,
  getClassicRuneIconUrl,
  getClassicItemIconUrl,
  type ClassicChampionData,
  type ClassicChampionSkillsMini,
  type ClassicMasteryDisplay,
  type ClassicMasteryTree,
  type ClassicMasteryNode,
  type ClassicSummonerSpell,
  type ClassicPerk,
  type ClassicRuneBuildFile,
  type ClassicItem,
  type ClassicItemBuildFile,
} from "@ugg/shared/hooks/classic";

// ═══════════════════════════════════════════════════════════════════════════
// League Classic champion profile ("Classic Build" page).
//
// This file is intentionally ONE big self-contained module. League Classic is
// not data-driven the way the normal champion pages are — its content comes
// from a separate hand-crafted cdragon pipeline (see @ugg/shared/hooks/classic)
// and the modules themselves ("Masteries", "Champion Info", "Skill Tooltips")
// have no modern equivalent to reuse. Keeping every Classic-only presentational
// piece here means that when the patch lands and Classic can (maybe) rejoin the
// standard riot-assets build pages, we can pivot by swapping this one file out
// rather than untangling custom code sprinkled across the repo.
//
// Data hooks live in @ugg/shared/hooks/classic (shared with the tier list); a
// handful of genuinely cross-cutting hookups (SEO entry, the shared page nav)
// live in their normal homes because they can't be localized here.
//
// Layout goal: read as close to the real Build page as possible so navigating
// between a champion's normal build and Classic build is not jarring. We reuse
// the legacy build SCSS class names (`champion-recommended-build`,
// `content-section*`, `toughest-matchups`, `recommended-build_*`,
// `skill-path-container`, …) so the modules inherit the same styling, and we
// reuse a few presentational shared components verbatim (Plus CTAs, promos).
//
// Much of the build content is still placeholder: runes, summoner spells and
// items render empty slots at the correct size to be filled in once we design
// the custom modules and wire their JSON. Masteries / Champion Info / Skills /
// Skill Tooltips render from real fetched data. Where a build feed doesn't
// exist yet (skill order, active mastery build, matchup list) we use the
// hardcoded TEST_* data below as a starting point to iterate from.
// ═══════════════════════════════════════════════════════════════════════════

// ─── Hardcoded starter data (swap for real feeds later) ──────────────────────

// Toughest-matchups roster — Classic champion keys. Hardcoded for now; will be
// driven by the Classic counters feed once it exists. (Annie, Olaf, TF.)
const TEST_MATCHUP_IDS = [60001, 60002, 60004];

// Rune-type groups in the canonical old-runes order, each with a colored accent.
// Shared by the compact runes panel and the Rune Summary module.
const RUNE_CATEGORIES: Array<{ key: keyof ClassicRuneBuildFile["runes"]; label: string; color: string }> = [
  { key: "kQuintessence", label: "Quintessence", color: "#F9BE90" },
  { key: "kMark", label: "Mark", color: "#B55A40" },
  { key: "kSeal", label: "Seal", color: "#FFE887" },
  { key: "kGlyph", label: "Glyph", color: "#71AFD5" },
];

// ─── Defensive coercions ─────────────────────────────────────────────────────
// The Classic JSON is hand-authored and its pipeline is still moving, so a field
// can arrive with an unexpected shape (or get renamed) without TypeScript noticing
// — riot data shifts, and we iterate fast. These keep a bad shape rendering empty
// instead of throwing (`.split` on a non-string, `.map`/`.forEach` on a non-array,
// …); missing/empty data on the page always beats a crashed page. Applied at the
// points where fetched data is first split/iterated.
const asText = (value: unknown): string => (typeof value === "string" ? value : "");
function asArray<T>(value: readonly T[] | null | undefined): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

// ─── Skill tooltip rendering ────────────────────────────────────────────────

// Color map for the custom markup tags used in the champion JSON's
// dynamicDescription (e.g. <magicDamage>…</magicDamage>). Unknown tags render
// with inherited color. Tuned to read like the in-client tooltip colors.
const SKILL_TAG_COLORS: Record<string, string> = {
  magicDamage: "#31c8e6",
  physicalDamage: "#ff9b6b",
  trueDamage: "#e0c56e",
  scaleAP: "#31c8e6",
  scaleAD: "#ff9b6b",
  scaleHealth: "#8fce5b",
  scaleMana: "#5db6ff",
  scaleArmor: "#f0b072",
  scaleMR: "#8fd6ff",
  healing: "#4bd15a",
  lifeSteal: "#4bd15a",
  shield: "#c9c9d6",
  speed: "#e0a458",
  status: "#ffffff",
  keyword: "#f0e6d2",
  keywordMajor: "#f0e6d2",
  spellName: "#d5a353",
  armorPen: "#ff9b6b",
  magicPen: "#31c8e6",
  attackSpeed: "#e0a458",
  scaleCrit: "#ff9b6b",
  rules: "#8b8ba7",
  flavorText: "#8b8ba7",
};

// Parse a dynamicDescription into React nodes: resolve the color tags, turn
// <br> into line breaks, drop the @SpellModifierDescriptionAppend@ hook, and
// replace every other @Placeholder@ token with a "?" (inheriting the current
// tag color). `hasMissing` is true when any "?" is emitted or already present,
// which drives the "? indicates missing/incorrect data" footnote.
function renderDynamicDescription(text: string | undefined): { node: ReactNode; hasMissing: boolean } {
  if (typeof text !== "string" || !text) return { node: null, hasMissing: false };

  let hasMissing = false;
  const tokens = text.split(/(<br\s*\/?>|<\/?[a-zA-Z][a-zA-Z0-9]*>|@[^@]+@)/g);
  const colorStack: Array<string | undefined> = [];
  const nodes: ReactNode[] = [];
  let key = 0;

  for (const tok of tokens) {
    if (!tok) continue;

    if (/^<br\s*\/?>$/.test(tok)) {
      nodes.push(<br key={key++} />);
      continue;
    }
    const open = tok.match(/^<([a-zA-Z][a-zA-Z0-9]*)>$/);
    if (open) {
      colorStack.push(SKILL_TAG_COLORS[open[1]]);
      continue;
    }
    const close = tok.match(/^<\/([a-zA-Z][a-zA-Z0-9]*)>$/);
    if (close) {
      colorStack.pop();
      continue;
    }
    const at = tok.match(/^@([^@]+)@$/);
    if (at) {
      // Text-append hook, not a value — render nothing.
      if (at[1] === "SpellModifierDescriptionAppend") continue;
      hasMissing = true;
      const color = colorStack[colorStack.length - 1];
      nodes.push(
        <span key={key++} style={color ? { color } : undefined}>
          ?
        </span>,
      );
      continue;
    }

    if (tok.includes("?")) hasMissing = true;
    const color = colorStack[colorStack.length - 1];
    nodes.push(
      color ? (
        <span key={key++} style={{ color }}>
          {tok}
        </span>
      ) : (
        <Fragment key={key++}>{tok}</Fragment>
      ),
    );
  }

  return { node: <>{nodes}</>, hasMissing };
}

// Collapse a per-rank cooldown array to a display string: a single value when
// every rank shares it, otherwise the "a/b/c" ladder.
function formatCooldown(arr: number[] | undefined): string | null {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return new Set(arr).size === 1 ? String(arr[0]) : arr.join("/");
}

// Shared tooltip body used both for hover tooltips (Skills Overview + header
// skill icons) and inline in the Skill Tooltips debug module.
function SkillTooltipContent(props: {
  name: string;
  cooldown?: string | null;
  description: string | undefined;
  isPassive?: boolean;
}) {
  const { name, cooldown, description, isPassive } = props;
  const { node, hasMissing } = renderDynamicDescription(description);

  return (
    <div className="max-w-[320px] text-[13px] leading-[18px]">
      <div className="font-bold text-accent-blue-400">{name}</div>
      {!isPassive && cooldown && <div className="mt-[2px] text-[12px] text-lavender-50">Cooldown: {cooldown}</div>}
      <div className="mt-[6px] text-white">{node}</div>
      {hasMissing && (
        <div className="mt-[8px] text-[11px] text-[#ffa514]">"?" indicates missing or incorrect data from Riot API</div>
      )}
    </div>
  );
}

// Role query-param default (drives labels + matchup links). Role option chips
// reuse the shared ROLE_OPTIONS (with the site's role icons).
const DEFAULT_ROLE = "mid";

// ═══════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════

export function ClassicChampionProfile({
  bottomAd,
  autoImportButton,
}: {
  bottomAd?: ReactNode;
  // Injected from the web shell so we can reuse the real AppDownloadButton
  // (which lives in the web layer) for exact style/size parity.
  autoImportButton?: ReactNode;
}) {
  // On the classic route (/lol/champions/classic/:champion-classic/:role?) the
  // `-classic` suffix is literal, so `champion` resolves to the bare alias
  // ("ahri"); `role` is the optional trailing path segment (SEO-friendly, in
  // place of the old ?role= query param).
  const { champion: alias, role: roleParam } = useParams<{ champion: string; role?: string }>();

  const { data: manifest } = useClassicChampions({ ssr: true });
  const { championKey, displayName } = useMemo(() => {
    const match = Object.entries(manifest || {}).find(([, entry]) => entry?.[0] === alias);
    return { championKey: match?.[0], displayName: match?.[1]?.[1] };
  }, [manifest, alias]);

  const { data: champion } = useClassicChampionJSON<ClassicChampionData>({ championKey: championKey || "", ssr: true });
  const { data: masteryDisplay } = useClassicMasteryDisplay({ ssr: true });

  const { data: uggPatches } = useUGGPatches({ ssr: true });
  const location = useLocation();

  // Patch filter — the real, working version selector (was a mocked single-
  // option select). The window mirrors the Classic tier list exactly: the
  // newest `CLASSIC_PATCH_OPTION.max` u.gg patches. Options are derived from the
  // SSR-fetched `uggPatches` (rather than usePatchOptions' own non-SSR fetch) so
  // they're present on the server render too, matching usePatchOptions' output
  // (same slice + same "26.14" label). The selection lives in the `?patch=`
  // query param; an absent/invalid value falls back to latest, so the canonical
  // (latest) URL carries no patch param.
  const patchOptions = useMemo(
    () =>
      (uggPatches || [])
        .slice(0, CLASSIC_PATCH_OPTION.max)
        .map((el) => ({ value: el, label: convertPatchDisplay(el).replace("_", ".") })),
    [uggPatches],
  );
  const latestPatch = uggPatches?.[0];
  const queryPatch = new URLSearchParams(location.search).get("patch") || undefined;
  const patch = queryPatch && patchOptions.some((o) => o.value === queryPatch) ? queryPatch : latestPatch;
  const patchDisplay = convertPatchDisplay(patch || "").replace("_", ".");

  // Curated skill priority — the compact "mini" feed (a flat ["Q","W","E"]
  // array keyed by champion). Only some champions have data yet; a missing file
  // → undefined → blank placeholder slots. (The full useClassicChampionSkills
  // feed is retained in the hooks module for when we resurface the leveling path.)
  const { data: skillsMini } = useClassicChampionSkillsMini({
    championKey: championKey || "",
    patch,
    ssr: true,
  });

  // Single curated mastery tree, keyed by champion. Most champions have no file
  // yet → empty selection, so the tree renders with no allocations (no fallback).
  const { selected: masterySelected } = useClassicChampionMastery({ championKey, patch, ssr: true });

  // Summoner spells: the champion-specific recommended pairing (keyed by champion
  // key, like skills/masteries/items) + the manifest that resolves ids →
  // names/tooltips. Champions without a build file yet → no data (empty module).
  const { data: spellBuild } = useClassicSpellBuild({ name: championKey || "", patch, ssr: true });
  const { data: spellManifest } = useClassicSpells({ ssr: true });
  const spellsById = useMemo(() => {
    const map: Record<number, ClassicSummonerSpell> = {};
    asArray(spellManifest).forEach((s) => (map[s.id] = s));
    return map;
  }, [spellManifest]);

  // Runes: the champion-specific perk build (keyed by champion key, like the
  // other feeds) + the perk manifest that resolves ids → title/effect/icon.
  // Champions without a build file yet → no data (empty module).
  const { data: runeBuild } = useClassicRuneBuild({ name: championKey || "", patch, ssr: true });
  const { data: perkManifest } = useClassicPerks({ ssr: true });
  const perksById = useMemo(() => {
    const map: Record<number, ClassicPerk> = {};
    asArray(perkManifest).forEach((p) => (map[p.id] = p));
    return map;
  }, [perkManifest]);

  // Items: the champion-specific build (keyed by champion key, like skills and
  // masteries), the mini id→name manifest for fast icon/name render, and the
  // full manifest (loaded async) for tooltips. Champions without a build file
  // yet resolve to no data → the module renders its empty placeholders.
  const { data: itemBuild } = useClassicItemBuild({ name: championKey || "", patch, ssr: true });
  const { data: itemsMini } = useClassicItemsMini({ ssr: true });
  const { data: itemsFull } = useClassicItems();
  const itemsById = useMemo(() => {
    const map: Record<number, ClassicItem> = {};
    asArray(itemsFull).forEach((i) => (map[i.id] = i));
    return map;
  }, [itemsFull]);

  const getClassicSEO = useClassicChampionSEO();

  // Classic tier list (single per-patch feed, shared with the tier-list page).
  // Used to derive: (1) the champion's "core role" — highest-tier role, ties
  // broken by first-listed — which seeds the default role selection, and
  // (2) the per-role tier used on the portrait + in SEO copy. Keyed to the
  // selected patch so the tier data follows the version filter (the hook
  // validates `patch` against the Classic params and falls back to latest).
  const { data: tierListData } = useClassicChampionTierList({ params: { patch }, ssr: true });
  const { coreRole, coreTier, roleTierByLetter } = useMemo(() => {
    const tierlist = tierListData?.tierlist;
    const keyNum = Number(championKey);
    const byLetter: Partial<Record<string, TierNameValue>> = {};
    let core: { letter?: string; tier?: TierNameValue } = {};
    if (tierlist && championKey) {
      // ALL_TIERS is ordered best → worst, so the first entry we hit for this
      // champion is its best tier; the first such entry overall is the core role.
      for (const tier of ALL_TIERS) {
        // asArray + Array.isArray guard: a malformed tier bucket (non-array) or a
        // non-pair entry must not throw here — this runs in render, so a throw
        // would crash the whole page. Number() compare so a string-keyed feed
        // ("60001") still matches instead of silently finding no core role.
        for (const entry of asArray(tierlist[tier])) {
          if (!Array.isArray(entry) || Number(entry[0]) !== keyNum) continue;
          const up = String(entry[1]).toUpperCase();
          if (!byLetter[up]) byLetter[up] = tier;
          if (!core.letter) core = { letter: up, tier };
        }
      }
    }
    return { coreRole: getClassicRoleFromLetter(core.letter), coreTier: core.tier, roleTierByLetter: byLetter };
  }, [tierListData, championKey]);

  // Role in the URL path wins; otherwise default to the champion's core role
  // (falling back to mid until the tier list resolves / if it has no entry).
  const roleInPath = roleParam && isSRMainRole(roleParam) ? (normalizeRole(roleParam) as string) : undefined;
  const role = roleInPath ?? coreRole ?? DEFAULT_ROLE;
  const roleLabel = getRoleLabel(role, (label) => (label === "Bottom" ? "ADC" : label));
  const name = displayName ?? alias;

  // Tier to show for the current role: the role's own tier when it has one,
  // else the core-role tier (a short-term SEO stopgap — we keep the current
  // role's text everywhere but borrow the core tier when a role lacks data).
  const roleLetter = getClassicRoleLetter(role);
  const displayTier = (roleLetter && roleTierByLetter[roleLetter]) || coreTier;

  // Scroll to top when the champion changes, not just on first mount: navigating
  // between two Classic champions reuses this same route (`.../classic/:champion`),
  // so React Router keeps this component mounted. Keyed on `alias` (the champion),
  // NOT the full location, so switching the role filter on the same champion keeps
  // the user's scroll position.
  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, [alias]);

  return (
    <div className="relative z-[1] w-[1014px] max-md:w-full mx-auto pt-[38px] max-sm:pt-[24px]">
      {getClassicSEO({ championName: displayName, alias, patch, roleInPath })}

      <ClassicHeader
        championKey={championKey}
        name={name}
        champion={champion}
        roleLabel={roleLabel}
        patch={patchDisplay}
        tier={displayTier}
      />

      {/* Same nav as the normal champion page: "Classic" is highlighted here
          (its activePaths match this route), "Build" links back to the normal
          build, and every other tab targets the bare champion alias. */}
      <ChampionProfilePageNav />

      <ClassicFilterBar
        className="my-[24px]"
        role={role}
        alias={alias}
        patchOptions={patchOptions}
        selectedPatch={patch || ""}
        latestPatch={latestPatch}
      />

      <div className="champion-recommended-build flex flex-col gap-[12px]">
        {/* Stats bar removed (reclaiming top space) — the Recommended panel is
            now the first module; the 12px flex gap separates the modules below. */}
        <ClassicRecommendedPanel
          name={name}
          roleLabel={roleLabel}
          spellIds={asArray(spellBuild?.spells)}
          spellsById={spellsById}
          runeBuild={runeBuild}
          perksById={perksById}
          autoImportButton={autoImportButton}
          championKey={championKey}
          champion={champion}
          skillsMini={skillsMini}
          itemBuild={itemBuild}
          itemsById={itemsById}
          itemsMini={itemsMini}
        />
        <ClassicMasteries masteryDisplay={masteryDisplay} selected={masterySelected} name={name} roleLabel={roleLabel} />
        {false && <ClassicToughestMatchups manifest={manifest} name={name} role={role} />}
        {/* Standalone items module removed — items now live in the Recommended panel. */}
        {false && <ClassicItems itemBuild={itemBuild} itemsById={itemsById} itemsMini={itemsMini} />}
        {false && <ClassicChampionInfo champion={champion} />}
        {true && <ClassicSkillsDetail championKey={championKey} champion={champion} />}
        {false && <ClassicRuneSummary runeBuild={runeBuild} perksById={perksById} name={name} roleLabel={roleLabel} />}
        {false && <ClassicMasterySummary masteryDisplay={masteryDisplay} selected={masterySelected} />}
        {false && (
          <ClassicItemsSummary endgame={asArray(itemBuild?.items?.endgame)} itemsById={itemsById} itemsMini={itemsMini} />
        )}
        {bottomAd}
      </div>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function ClassicHeader(props: {
  championKey?: string;
  name: string;
  champion?: ClassicChampionData;
  roleLabel: string;
  patch: string;
  tier?: TierNameValue;
}) {
  const { championKey, name, champion, roleLabel, patch, tier } = props;

  const skillSlots: Array<{ key: "p" | "q" | "w" | "e" | "r"; label: string }> = [
    { key: "p", label: "P" },
    { key: "q", label: "Q" },
    { key: "w", label: "W" },
    { key: "e", label: "E" },
    { key: "r", label: "R" },
  ];

  const spellByKey = (key: string) => asArray(champion?.spells).find((s) => s.spellKey === key);

  // Tier-specific portrait border + badge color, mirroring the normal build
  // page's ChampionIconContainer (tierColor on both borders, white badge text).
  // getTierGradeColorHex maps the letter grade → the shared TIER_GRADE_STYLES
  // color (S+ orange, S blue, …); falls back to the neutral frame until a tier
  // resolves / when the champion has no tier-list entry.
  const tierColor = tier ? getTierGradeColorHex(tier) : "#414165";

  // SEO copy — rendered twice (like the normal champion header): inline beside
  // the skill icons on tablet/desktop, and again below the whole header on
  // mobile (small screens), so mobile still gets the crawlable text.
  const seoText = `U.GG ${name} Classic build shows best ${name} Classic runes and masteries by the best players. With skill order and items, this ${name} guide offers a full LoL ${name} Classic build for Patch ${patch}`;

  return (
    <div className="mb-[24px]">
      <div className="flex items-start w-full max-sm:relative">
        {/* Portrait frame mirrors the normal header's ChampionIconContainer
            (93px, tier-colored border + centered tier badge). Tier comes from the
            Classic tier list (the selected role's tier, or the core-role tier as
            a fallback); "?" until it resolves / if there's no tier-list entry. */}
        <div
          className="relative flex-none w-[93px] h-[93px] rounded-[6px] border-[2px] bg-[#17172e]"
          style={{ borderColor: tierColor }}
        >
          <div
            className="absolute z-[2] top-[-16px] left-[50%] translate-x-[-50%] flex items-center justify-center w-[36px] h-[20px] rounded-[4px] border-[1px] bg-[#06061f] text-white text-[11px] font-bold"
            style={{ borderColor: tierColor }}
          >
            {tier || "?"}
          </div>
          <div className="relative w-full h-full rounded-[4px] border-[2px] border-[#17172e] overflow-hidden">
            <div className="absolute top-[-6px] left-[50%] z-[1] translate-x-[-50%] w-[40px] h-[10px] rounded-b-[2px] border-[2px] border-purple-500 bg-[#06061f]" />
            {championKey && (
              <img src={getClassicChampionImgUrl(championKey)} alt={name} className="w-full h-full rounded-[2px] scale-[1.2]" />
            )}
          </div>
        </div>
        <div className="flex-1 flex flex-col justify-between min-w-0 h-full ml-[24px] max-sm:ml-[12px] max-sm:self-end">
          {/* Mirrors the normal champion header exactly: min-md:whitespace-nowrap
              + max-sm:block so the title wraps/shrinks on mobile, and
              max-sm:mt-[-100px] (with the column's self-end + the 93px portrait)
              pulls the title up beside the portrait like the other pages. */}
          <h1 className="flex min-md:whitespace-nowrap items-center max-sm:items-start font-['Barlow'] max-sm:leading-[0.7] max-sm:mt-[-100px] max-sm:block">
            <span className="mr-[12px] text-[36px] font-semibold max-sm:text-[24px] max-sm:mr-[6px]">{name}</span>
            <span className="text-[36px] font-normal text-lavender-50 max-sm:w-full max-sm:text-[20px]">
              Classic Build for {roleLabel}
            </span>
            {patch && (
              <MediaQuery min="TABLET" max="DESKTOP_LARGE">
                <HeaderPatch className="ml-[10px]" patch={patch} />
              </MediaQuery>
            )}
          </h1>
          <div className="flex items-center h-[32px] mt-[18px] max-sm:mt-[12px]">
            <div className="flex-none flex items-center mr-[24px] gap-[6px]">
              {skillSlots.map(({ key, label }) => {
                const isPassive = key === "p";
                const spell = spellByKey(key);
                const tipName = isPassive ? champion?.passive?.name : spell?.name;
                return (
                  <TooltipContainer
                    key={key}
                    disableTooltip={!champion}
                    tooltipInfo={
                      <SkillTooltipContent
                        name={tipName || label}
                        cooldown={formatCooldown(spell?.cooldownCoefficients)}
                        description={isPassive ? champion?.passive?.description : spell?.dynamicDescription}
                        isPassive={isPassive}
                      />
                    }
                  >
                    <div className="relative">
                      {championKey && (
                        <img
                          src={getClassicChampionSkillImgUrl(championKey, key)}
                          alt={tipName || label}
                          className="w-[36px] h-[36px] rounded-[3px]"
                        />
                      )}
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex items-center justify-center w-[12px] h-[12px] rounded-full border-[2px] border-[#17172e] bg-[#383864] text-[9px] font-bold leading-none">
                        {label}
                      </div>
                    </div>
                  </TooltipContainer>
                );
              })}
            </div>
            {/* Inline SEO — tablet/desktop only (hidden on mobile; shown below). */}
            <div className="pr-[12px] text-lavender-50 text-[12px] max-sm:text-[11px] font-medium max-xs:hidden">
              {seoText}
            </div>
          </div>
        </div>
      </div>
      {/* Mobile SEO — under the portrait/skills (matches the normal champion
          header's second SEO block); the inline copy above is hidden here. */}
      <div className="mt-[24px] pr-[12px] max-sm:mt-[18px] text-lavender-50 text-[11px] font-medium xs:hidden">
        {seoText}
      </div>
    </div>
  );
}

// ─── Filter bar (stripped: role always visible; queue/patch/worlds under More) ─

// Filter bar — mirrors the site's FilterManager markup (wrapper + funnel label)
// so it reads identically to the normal build page. Everything is shown inline
// (no More/Less). Role is the site's 5-button role split (crawlable role Links,
// matching the normal build page); Queue is locked to Classic (only mode on
// this route); Patch is the real version selector (same window as the Classic
// tier list, driven by the `?patch=` query param); Region is Worlds-only
// (reusing REGION_OPTIONS_ONLY_WORLD so it renders the same globe as elsewhere).
type ClassicQueueOption = { value: string; label: string; path: string; reset?: string[] };
type ClassicPatchOption = { value: string; label: string };

function ClassicFilterBar(props: {
  className?: string;
  role: string;
  alias: string;
  // Real patch window (newest N patches, matching the tier list). `selectedPatch`
  // is the active raw value ("16_14"); `latestPatch` is the newest raw value —
  // selecting it drops the `?patch=` param so the canonical URL stays clean.
  patchOptions: ClassicPatchOption[];
  selectedPatch: string;
  latestPatch?: string;
}) {
  const { className, role, alias, patchOptions, selectedPatch, latestPatch } = props;
  const history = useHistory();
  const location = useLocation();

  // Queue selector — the same game-mode options as the normal build page, plus a
  // "Classic" entry for the current mode. Selecting another mode navigates to
  // that mode's build page for this champion, compiling the option's route with
  // the champion alias (mirroring the FilterManager's path handling) and carrying
  // queueType as a query param unless the option resets it (special modes do).
  const queueOptions: ClassicQueueOption[] = [
    { value: "classic", label: "Classic", path: AppRoutes.CHAMPION_PROFILE_BUILD_CLASSIC },
    ...OVERVIEW_QUEUE_TYPE_OPTIONS,
  ];

  const onQueueChange = (option: ClassicQueueOption) => {
    if (option.value === "classic") return; // already on the Classic page
    const pathname = compile(option.path, { encode: encodeURIComponent })({ champion: alias });
    const params: Record<string, string> = { queueType: option.value };
    (option.reset || []).forEach((key) => delete params[key]);
    history.push({ pathname, search: buildQueryParams(params) });
  };

  // Patch selector — set/clear the `?patch=` query param while keeping the
  // current pathname (role route + champion). Selecting the latest patch drops
  // the param (latest is canonical, no param); older patches set it. Other
  // query params are preserved.
  const onPatchChange = (option: ClassicPatchOption) => {
    if (option.value === selectedPatch) return;
    const search = new URLSearchParams(location.search);
    if (!latestPatch || option.value === latestPatch) search.delete("patch");
    else search.set("patch", option.value);
    const qs = search.toString();
    history.push({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
  };

  return (
    <FilterManagerWrapper className={className}>
      <div className="filter-manager_label">
        <FilterIcon />
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <span>Filters</span>
        </MediaQuery>
      </div>
      <div className="flex-1 flex items-center flex-wrap gap-[12px]">
        {/* Role split — same markup/CSS as the normal build page's
            ChampionOverviewRoleSplit. Each role is a Link to its build-route
            path segment (SEO-friendly + crawlable), highlighting the active role. */}
        <div className="role-filter-container">
          {ROLE_OPTIONS.map((option) => {
            const isSelected = normalizeRole(role) === normalizeRole(option.value);
            return (
              <TooltipContainer key={option.value} tooltipInfo={option.label}>
                <Link
                  className={classNames("role-filter", { active: isSelected })}
                  to={getClassicChampionBuildUrl(alias, { role: option.value })}
                >
                  {isSelected && option.imgActive ? option.imgActive : option.img}
                </Link>
              </TooltipContainer>
            );
          })}
        </div>
        {/* Queue selector — switch to any other game mode's build for this
            champion (Classic is the current selection). */}
        <DefaultSelect
          className="filter-select queue-type"
          options={queueOptions}
          value="classic"
          onChange={onQueueChange}
          formatOptionLabel={(o) => <div>{o.label}</div>}
          width={150}
          isSearchable={false}
        />
        {/* Patch selector — the newest N Classic patches (same window as the
            tier list). Changing it drives the `?patch=` query param. */}
        <DefaultSelect
          className="filter-select"
          options={patchOptions}
          value={selectedPatch}
          onChange={onPatchChange}
          width={110}
          isSearchable={false}
        />
        {/* Worlds-only region (matches the ARAM Mayhem augment page). */}
        <DefaultSelect
          className="filter-select"
          options={REGION_OPTIONS_ONLY_WORLD}
          value="world"
          onChange={() => {}}
          formatOptionLabel={({ label, img, flag }) => (
            <div className="region-option">
              <div className="region-img-wrapper">
                <img className={`region-img ${flag ? "flag-img" : ""}`} src={img as string} />
              </div>
              <span style={{ marginLeft: "10px" }}>{label}</span>
            </div>
          )}
          width={120}
          isSearchable={false}
        />
      </div>
    </FilterManagerWrapper>
  );
}

// ─── Recommended panel (tab + auto-import + warning; runes/spells placeholders) ─

function ClassicRecommendedPanel(props: {
  name: string;
  roleLabel: string;
  spellIds: number[];
  spellsById: Record<number, ClassicSummonerSpell>;
  runeBuild?: ClassicRuneBuildFile;
  perksById: Record<number, ClassicPerk>;
  autoImportButton?: ReactNode;
  // Skill Priority lives inside the summoner-spells cell (its own dedicated
  // module below was removed), resolved from the compact "mini" priority feed.
  championKey?: string;
  champion?: ClassicChampionData;
  skillsMini?: ClassicChampionSkillsMini;
  // Item build now lives at the bottom of the right column (replacing the CTA).
  itemBuild?: ClassicItemBuildFile;
  itemsById: Record<number, ClassicItem>;
  itemsMini?: Record<string, string>;
}) {
  const { name, roleLabel, spellIds, spellsById, runeBuild, perksById, autoImportButton } = props;
  const { championKey, champion, skillsMini } = props;
  const { itemBuild, itemsById, itemsMini } = props;

  const priorityKeys = resolveSkillPriorityMini(skillsMini);

  const warningTip = (
    <div className="max-w-[300px] text-[13px]">Classic builds are a work in progress — module content is coming soon.</div>
  );

  // Item build for the new items module (bottom of the right column). Starting
  // Items = the opening buy (duplicate ids → a stacked consumable, shown with a
  // quantity badge); Final Build = the finished ≤6-item build. Situational Items
  // has no curated feed yet, so it renders placeholder slots at the right size.
  const starter = asArray(itemBuild?.items?.starter);
  const endgame = asArray(itemBuild?.items?.endgame);
  const starterCounts = new Map<number, number>();
  starter.forEach((id) => starterCounts.set(id, (starterCounts.get(id) || 0) + 1));
  const starterUnique = [...starterCounts.keys()];
  const emptyItem = <div className="flex-none rounded-[3px] bg-[#25254b]/50" style={{ width: 36, height: 36 }} />;
  const renderItem = (id: number, quantity?: number) => (
    <ClassicItemIcon itemId={id} item={itemsById[id]} name={itemsMini?.[String(id)]} quantity={quantity} />
  );

  return (
    <div className="rune-spell">
      <div className="flex h-[42px]">
        <div className="flex-1">
          {/* Static "Recommended" tab, styled like BuildTabs' active default tab.
              The rune/item icons were removed with the multibuild filter. */}
          <div
            className="inline-flex items-center px-[12px] py-[10px] h-[42px] rounded-t-[3px] shadow-[inset_0px_2px_0px_#ff9b00]"
            style={{ backgroundColor: "#11112A" }}
          >
            <strong className="font-['Inter'] text-[14px] leading-[20px] text-white font-bold whitespace-nowrap">
              Recommended
            </strong>
          </div>
        </div>
        {/* Reuses the site's AppDownloadButton, injected from the web shell. */}
        {autoImportButton && (
          <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
            {autoImportButton}
          </MediaQuery>
        )}
        <TooltipContainer tooltipInfo={warningTip} customStyle={{ maxWidth: "400px" }} tooltipHoverable>
          <div className="flex items-center justify-center ml-[6px] w-[34px] h-[34px] bg-purple-400 rounded-[3px]">
            <Warning className="flex-none w-[18px] h-[18px]" />
          </div>
        </TooltipContainer>
      </div>

      {/* Module body — custom flex layout (the legacy `.grid-1` grid was replaced
          because its `min-content 1fr` rows grew the summoner cell when we forced
          the module height). Kept the `.content-section content-section_unset`
          wrapper only so the nested `.content-section_header` blue-bar styling
          still applies. Two columns: left runes (#11112A, flex-1) and a fixed
          443px right column (#191937) split into a ~110px summoner/skill section
          over a flex-1 items section. minHeight hits the figma 417px. */}
      <div className="content-section content-section_unset flex max-sm:flex-col" style={{ minHeight: 417 }}>
        {/* LEFT — runes (#11112A) */}
        <div
          className="flex-1 min-w-0 flex flex-col p-[18px] max-sm:p-[12px]"
          style={{ backgroundColor: "#11112A", borderRight: "1px solid #070720" }}
        >
          <div className="content-section_header">
            <div className="main-header">
              <span style={{ marginRight: 12 }}>{`${name} Runes`}</span>
              <MediaQuery min="TABLET" max="DESKTOP_LARGE">
                <span className="content-section_blurb truncate" style={{ direction: "rtl" }}>
                  {`${name} ${roleLabel} Build`}
                </span>
              </MediaQuery>
            </div>
          </div>
          <ClassicRunesCompact runeBuild={runeBuild} perksById={perksById} className="flex-1" />
        </div>

        {/* RIGHT — fixed 443px column (#191937): summoner/skill over items.
            maxWidth caps it so it collapses instead of overflowing on mobile. */}
        <div className="flex-none flex flex-col" style={{ width: 443, maxWidth: "100%", backgroundColor: "#191937" }}>
          {/* Top: Summoner Spells (170px) | 1px #070720 divider | Skill Priority
              (fills the rest, ~272px). Padding per column: 18px top → text, 18px
              header→icons (header margin-bottom), 12px between summoner icons, 8px
              icon↔arrow in skill. 18px bottom padding leaves 18px under the summoner
              icons; the skill keybind badge overhangs ~6px so ~12px shows under it. */}
          <div className="flex flex-none">
            {/* Summoner Spells — 170px wide */}
            <div className="flex-none p-[18px] max-sm:p-[12px]" style={{ width: 170 }}>
              <div className="content-section_header">
                <div>Summoner Spells</div>
              </div>
              <div className="flex gap-[12px]">
                {spellIds.length > 0
                  ? spellIds
                      .slice(0, 2)
                      .map((id, i) => <ClassicSummonerSpellIcon key={`${id}-${i}`} spellId={id} spell={spellsById[id]} />)
                  : [0, 1].map((i) => (
                      <div key={i} className="flex-none rounded-[3px] bg-[#25254b]/50" style={{ width: 36, height: 36 }} />
                    ))}
              </div>
            </div>

            {/* 1px #070720 divider, full section height */}
            <div className="flex-none self-stretch w-px" style={{ backgroundColor: "#070720" }} />

            {/* Skill Priority — fills the rest (~272px) */}
            <div className="flex-1 min-w-0 p-[18px] max-sm:p-[12px]">
              <div className="content-section_header">
                <div>Skill Priority</div>
              </div>
              <div className="flex flex-wrap items-center gap-y-[6px]">
                {priorityKeys && priorityKeys.length > 0
                  ? priorityKeys.map((key, index) => (
                      <Fragment key={`${key}-${index}`}>
                        <ClassicSkillIcon
                          championKey={championKey}
                          champion={champion}
                          skillKey={key as "q" | "w" | "e" | "r"}
                          className="rounded-[3px]"
                        />
                        {index !== priorityKeys.length - 1 && <ArrowRight className="mx-[8px] w-[12px] h-[12px] flex-none" />}
                      </Fragment>
                    ))
                  : [0, 1, 2].map((i) => (
                      <Fragment key={i}>
                        <div className="flex-none rounded-[3px] bg-[#25254b]/50" style={{ width: 36, height: 36 }} />
                        {i !== 2 && <ArrowRight className="mx-[8px] w-[12px] h-[12px] flex-none" />}
                      </Fragment>
                    ))}
              </div>
            </div>
          </div>

          {/* Horizontal #070720 groove between the top section and the items. */}
          <div className="flex-none" style={{ height: 1, backgroundColor: "#070720" }} />

          {/* Bottom: items (fills the rest). Starting / Final Build / Situational. */}
          <div className="flex-1 p-[18px] max-sm:p-[12px]">
            <div className="content-section_header">
              <div>Starting Items</div>
            </div>
            <div className="flex flex-wrap gap-[12px]">
              {starterUnique.length > 0
                ? starterUnique.map((id) => <Fragment key={id}>{renderItem(id, starterCounts.get(id))}</Fragment>)
                : [0, 1].map((i) => <Fragment key={i}>{emptyItem}</Fragment>)}
            </div>

            <div className="content-section_header mt-[24px]">
              <div>Final Build</div>
            </div>
            <div className="flex flex-wrap gap-[12px]">
              {endgame.length > 0
                ? endgame.slice(0, 6).map((id, i) => <Fragment key={`${id}-${i}`}>{renderItem(id)}</Fragment>)
                : [0, 1, 2, 3, 4, 5].map((i) => <Fragment key={i}>{emptyItem}</Fragment>)}
            </div>

            {/* Situational Items — no curated feed yet, so placeholders for now. */}
            <div className="content-section_header mt-[24px]">
              <div>Situational Items</div>
            </div>
            <div className="flex flex-wrap gap-[12px]">
              {[0, 1, 2].map((i) => (
                <Fragment key={i}>{emptyItem}</Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Compact runes for the Recommended panel. SHORT-TERM layout: with sparse data
// the old four-column grid left most of the panel empty and read badly, so each
// rune is now its OWN card (Rune Summary content — quantity + icon + full title +
// effect) with a category-colored left accent, stacked in a SINGLE column of
// uniform full-width cards that stretch (flex-1) to fill the panel's height and
// its full width. Runes are flattened in canonical category order (Quintessence →
// Mark → Seal → Glyph). The #1C1C3C bordered panel and its caller-driven fill
// height (className, e.g. flex-1) are kept. `font-['Inter']` so the card text
// matches the rest of the redesigned modules.
function ClassicRunesCompact(props: {
  runeBuild?: ClassicRuneBuildFile;
  perksById: Record<number, ClassicPerk>;
  className?: string;
}) {
  const { runeBuild, perksById, className } = props;
  if (!runeBuild?.runes) return null;

  // Flatten every category's runes into individual cards, tagging each with its
  // category color for the accent bar (keeps the color-coding without headers).
  const cards = RUNE_CATEGORIES.flatMap((cat) =>
    asArray(runeBuild.runes[cat.key]).map((entry, i) => {
      const [perkId, count] = Array.isArray(entry) ? entry : [0, 0];
      return { key: `${cat.key}-${perkId}-${i}`, perkId, count, color: cat.color };
    }),
  );

  return (
    <div
      className={classNames("flex flex-col overflow-hidden rounded-[3px]", className)}
      // Colors + min-height set inline (not Tailwind arbitrary classes) so they
      // apply reliably; minHeight keeps the module at the figma ~417px.
      style={{ backgroundColor: "#1C1C3C", border: "1px solid #383864", minHeight: 341 }}
    >
      {/* Single column of full-width cards that share the panel height equally
          (each flex-1) — uniform width AND height, stretched to both edges. */}
      <div className="flex flex-1 flex-col gap-[12px] p-[14px]">
        {cards.map(({ key, perkId, count, color }) => {
          const perk = perksById[perkId];
          return (
            <div
              key={key}
              // flex-1 → equal-height cards filling the column; min-h keeps the
              // 36px icon from clipping if the list ever gets long enough that an
              // equal share would be shorter than the icon.
              className="relative flex flex-1 items-center gap-[10px] overflow-hidden rounded-[3px] bg-[#11112a] pl-[16px] pr-[12px] min-h-[52px] font-['Inter']"
            >
              <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: color }} />
              <div className="w-[24px] flex-none text-center text-[12px] font-bold text-white">{count}x</div>
              {perk?.iconPath ? (
                <img
                  src={getClassicRuneIconUrl(perk.iconPath)}
                  alt={perk.title}
                  loading="lazy"
                  className="flex-none w-[36px] h-[36px]"
                />
              ) : (
                <div className="flex-none w-[36px] h-[36px] rounded-full bg-[#25254b]/50" />
              )}
              <div className="min-w-0">
                <div className="text-[12px] font-bold text-white">{perk?.title ?? `Rune ${perkId}`}</div>
                <div className="text-[11px] leading-[15px] text-lavender-50">{perk?.tooltip}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A single recommended summoner spell: icon + hover tooltip (name, cooldown,
// description) resolved from the spell manifest. Renders the bare icon if the
// manifest hasn't resolved the id yet.
function ClassicSummonerSpellIcon(props: { spellId: number; spell?: ClassicSummonerSpell }) {
  const { spellId, spell } = props;
  const icon = (
    <img
      src={getClassicSpellIconUrl(spellId)}
      alt={spell?.name || `Spell ${spellId}`}
      className="w-[36px] h-[36px] rounded-[3px]"
    />
  );
  if (!spell) return icon;

  return (
    <TooltipContainer
      tooltipInfo={
        <div className="max-w-[280px] text-[13px] leading-[18px]">
          <div className="font-bold text-accent-blue-400">{spell.name}</div>
          {spell.cooldown ? <div className="mt-[2px] text-[12px] text-lavender-50">Cooldown: {spell.cooldown}s</div> : null}
          <div className="mt-[6px] text-white">{spell.description}</div>
        </div>
      }
    >
      {icon}
    </TooltipContainer>
  );
}

// ─── Masteries (tabbed, full width) ───────────────────────────────────────────

// Per-tree accent color — drives the header underline, the selected-node borders,
// and the active connectors (figma: Offense red, Defense blue, Utility green).
const MASTERY_TREE_COLOR: Record<string, string> = {
  offense: "#FF4655",
  defense: "#3273FA",
  utility: "#149C3A",
};
const MASTERY_CONNECTOR_OFF = "#33335a"; // dim — prerequisite not met

// Hex "#rrggbb" → "rgba(r, g, b, a)" — for the per-tree translucent header tint.
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Node grid geometry (px). Connectors run card-bottom → next card-top so they sit
// in the gap between nodes and never cross a node's rank label.
const MASTERY_ICON = 36;
// Row height sized so the tallest tree (6 old-mastery tiers) fits the 543px panel
// (header ~48 + node-area padding ~28 + 6 rows). Shorter trees leave bottom space.
const MASTERY_ROW_H = 78;
const MASTERY_NODE_PT = 6;
const MASTERY_CARD_BOTTOM = MASTERY_NODE_PT + MASTERY_ICON + 4 + 16; // pt + icon + gap + rank

function ClassicMasteries(props: {
  masteryDisplay?: ClassicMasteryDisplay;
  selected: Record<number, number>;
  name: string;
  roleLabel: string;
}) {
  const { masteryDisplay, selected, name, roleLabel } = props;
  const trees = asArray(masteryDisplay?.trees);

  return (
    <div className="content-section">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Masteries</span>
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <span className="content-section_blurb">{`These are the best masteries for ${name} ${roleLabel}`}</span>
        </MediaQuery>
      </div>

      {trees.length === 0 ? (
        <div className="flex items-center justify-center h-[200px] text-lavender-50">Loading masteries…</div>
      ) : (
        // paddingTop 1 → the header's 18px margin-bottom + 1 = 19px label→trees
        // (margins would collapse, padding doesn't). gap-[19px] between panels.
        <div className="grid grid-cols-3 gap-[19px] max-md:grid-cols-1" style={{ paddingTop: 1 }}>
          {trees.map((tree) => (
            <MasteryTreeColumn key={tree.name} tree={tree} selected={selected} />
          ))}
        </div>
      )}
    </div>
  );
}

// One tree column: accent header + connected node grid. (The category summary
// now lives full-width below all three trees — see MasteryCategorySummary.)
function MasteryTreeColumn(props: { tree: ClassicMasteryTree; selected: Record<number, number> }) {
  const { tree, selected } = props;
  const color = MASTERY_TREE_COLOR[tree.type] || MASTERY_TREE_COLOR.offense;

  // Connector routing depends only on the tree's structure (node positions +
  // prerequisite links), so memoize on [tree]. Without this, changing the role
  // filter — which re-renders this module — rebuilds the position Map and the
  // connector list for all three trees on every render.
  const connectors = useMemo(() => {
    const posById = new Map<number, { row: number; col: number }>();
    asArray(tree.rows).forEach((row, r) =>
      asArray(row.masteries).forEach((node, c) => node && posById.set(node.id, { row: r, col: c })),
    );

    const result: Array<{ col: number; fromRow: number; toRow: number; prereqId: number }> = [];
    asArray(tree.rows).forEach((row) =>
      asArray(row.masteries).forEach((node) => {
        if (node?.requiresMastery && posById.has(node.requiresMastery)) {
          const from = posById.get(node.requiresMastery)!;
          const to = posById.get(node.id)!;
          // Every prerequisite link in the data is a same-column drop; draw a
          // vertical line between them.
          if (from.col === to.col) result.push({ col: to.col, fromRow: from.row, toRow: to.row, prereqId: node.requiresMastery });
        }
      }),
    );
    return result;
  }, [tree]);

  const total = masteryTreeTotal(tree, selected);

  return (
    // Tree panel — 314×543 with a #383864 border (figma). bg + border inline so
    // they apply reliably over any legacy styling.
    <div
      className="relative overflow-hidden rounded-[3px]"
      style={{ minHeight: 543, backgroundColor: "#11112a", border: "1px solid #383864" }}
    >
      {/* Background art (already tinted per tree), pinned to the top and faded into
          the panel bg toward the bottom so the lower rows stay legible. The source
          art's bottom frame falls in the faded/clipped region. */}
      <img
        src={getClassicMasteryTreeBgUrl(tree.type)}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute left-0 top-0 w-full select-none"
        style={{ zIndex: 0 }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ zIndex: 0, background: "linear-gradient(180deg, rgba(17, 17, 42, 0.5) 0%, #11112A 100%)" }}
      />
      {/* Header (312×50): white name (left) + total pts (right), 15/17/18/17
          padding, with a tree-colored 1px underline + a 20%-opacity tree-color
          wash so it stands out from the faded art below (figma Rectangle 1797). */}
      <div
        className="relative flex items-center justify-between"
        style={{
          zIndex: 1,
          height: 50,
          paddingTop: 15,
          paddingBottom: 18,
          paddingLeft: 17,
          paddingRight: 17,
          backgroundColor: hexToRgba(color, 0.2),
          borderBottom: `1px solid ${color}`,
          borderRadius: "2px 2px 0 0",
        }}
      >
        <span style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 14, lineHeight: 1, color: "#ffffff" }}>{tree.name}</span>
        <span
          className="text-right"
          style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 14, lineHeight: 1, color: "#ffffff" }}
        >
          {total} <span className="text-[11px] font-medium text-lavender-50">pts</span>
        </span>
      </div>

      {/* Node grid — 4 columns of 36px icons, 34px between them + 34px on each
          edge (columnGap + padding). Vertical: paddingTop 19 + node paddingTop 6
          = 25px header→first icon; ROW_H 78 (icon 36 + 42 gap) → 42px icon-to-icon;
          paddingBottom 6 → the last row's icon sits 42px above the panel bottom. */}
      <div
        className="relative grid"
        style={{
          zIndex: 1,
          gridTemplateColumns: "repeat(4, minmax(0,1fr))",
          columnGap: 34,
          paddingLeft: 34,
          paddingRight: 34,
          paddingTop: 19,
          paddingBottom: 6,
          gridAutoRows: `${MASTERY_ROW_H}px`,
        }}
      >
        {/* Connectors sit behind the nodes, in the gaps between cards; the tree
            accent color when the prerequisite is allocated, else dim. */}
        {connectors.map((con, i) => {
          const active = (selected[con.prereqId] || 0) > 0;
          const height = (con.toRow - con.fromRow) * MASTERY_ROW_H - (MASTERY_CARD_BOTTOM - MASTERY_NODE_PT);
          return (
            <div
              key={i}
              className="relative pointer-events-none"
              style={{ gridColumn: con.col + 1, gridRow: `${con.fromRow + 1} / ${con.toRow + 2}`, zIndex: 0 }}
            >
              <div
                className="absolute left-1/2 -translate-x-1/2 rounded-full"
                style={{ top: MASTERY_CARD_BOTTOM, height, width: 2, backgroundColor: active ? color : MASTERY_CONNECTOR_OFF }}
              />
            </div>
          );
        })}

        {/* Node cells. */}
        {asArray(tree.rows).map((row, r) => (
          <Fragment key={r}>
            {asArray(row.masteries).map((node, c) =>
              node ? (
                <div
                  key={node.id}
                  className="flex justify-center"
                  style={{ gridColumn: c + 1, gridRow: r + 1, paddingTop: MASTERY_NODE_PT, zIndex: 1 }}
                >
                  <MasteryNode node={node} rank={selected[node.id] || 0} color={color} />
                </div>
              ) : null,
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// Sum of allocated ranks across a tree.
function masteryTreeTotal(tree: ClassicMasteryTree, selected: Record<number, number>): number {
  return asArray(tree.rows).reduce(
    (sum, row) => sum + asArray(row.masteries).reduce((s, m) => s + (m ? selected[m.id] || 0 : 0), 0),
    0,
  );
}

function MasteryNode(props: { node: ClassicMasteryNode; rank: number; color: string }) {
  const { node, rank, color } = props;
  const active = rank > 0;

  return (
    <TooltipContainer tooltipInfo={<MasteryNodeTooltip node={node} rank={rank} />}>
      <div className="flex flex-col items-center gap-[4px] cursor-default">
        {/* 36×36 icon; selected → 2px tree-color border (2px radius). The border
            is an overlay drawn ON TOP of the image so it covers the icon art's
            own light frame/corners, keeping the accent color crisp. (Kept at 2px
            from the edge — insetting it re-exposes the icon's own light border.) */}
        <div className="relative flex-none" style={{ width: MASTERY_ICON, height: MASTERY_ICON }}>
          <img
            src={getClassicMasteryIconUrl(node.id, active)}
            alt={node.name}
            loading="lazy"
            className={classNames("block", !active && "opacity-80")}
            style={{ width: MASTERY_ICON, height: MASTERY_ICON, borderRadius: 2 }}
          />
          {active && (
            <div className="absolute inset-0 pointer-events-none" style={{ borderRadius: 2, border: `2px solid ${color}` }} />
          )}
        </div>
        {/* Rank label — 28×16, radius 8, #0D0D28 bg; Inter 500 11px, white when
            allocated else #5C5C8E (figma). */}
        <div
          className="flex items-center justify-center"
          style={{
            width: 28,
            height: 16,
            borderRadius: 8,
            padding: 2,
            backgroundColor: "#0D0D28",
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: 11,
            lineHeight: "12px",
            color: active ? "#FFFFFF" : "#5C5C8E",
          }}
        >
          {rank}/{node.maxRank}
        </div>
      </div>
    </TooltipContainer>
  );
}

// Hover tooltip for a tree node — blue name, "Rank x / y", and the full ladder
// description with the active rank's value highlighted (see renderMasteryDescription).
// No icon, matching the rest of the site's tooltips.
function MasteryNodeTooltip(props: { node: ClassicMasteryNode; rank: number }) {
  const { node, rank } = props;
  return (
    <div className="max-w-[280px]">
      <div className="text-[14px] font-bold text-accent-blue-400">{node.name}</div>
      <div className="text-[12px] text-accent-gray-100">
        Rank {rank} / {node.maxRank}
      </div>
      <div className="mt-[8px] text-[13px] leading-[18px] text-white">{renderMasteryDescription(node.description, rank)}</div>
    </div>
  );
}

// ─── Toughest matchups (hardcoded Classic champ cards) ────────────────────────

function ClassicToughestMatchups(props: { manifest?: Record<string, [string, string]>; name: string; role: string }) {
  const { manifest, name, role } = props;

  return (
    <div className="content-section toughest-matchups">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Toughest Matchups</span>
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <span className="content-section_blurb">These champions counter {name}</span>
        </MediaQuery>
      </div>
      <div className="matchups">
        <div className="flex gap-3">
          {/* Left Plus CTA kept as-is to mirror the normal page. */}
          <PersonalizedMatchupsPlusCTA utmSource="classictoughestmatchups" />
          {TEST_MATCHUP_IDS.map((id) => {
            const entry = manifest?.[String(id)];
            const matchupAlias = entry?.[0];
            const matchupName = entry?.[1] ?? `#${id}`;
            const card = (
              <>
                <div className="champion-face">
                  <img src={getClassicChampionImgUrl(id)} alt={matchupName} />
                </div>
                <div className="champion-name">{matchupName}</div>
                <hr />
                {/* WR / matches intentionally blank until the counters feed exists. */}
                <div className="matchup-stats h-[26px]" />
              </>
            );

            return matchupAlias ? (
              <Link
                key={id}
                className="champion-matchup"
                to={{ pathname: getClassicChampionBuildUrl(matchupAlias, { role }), state: { scrollTop: true } }}
              >
                {card}
              </Link>
            ) : (
              <div key={id} className="champion-matchup">
                {card}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Skill priority (classic skill data) ─────────────────────────────────────
//
// Skill Priority renders inside the Recommended panel's summoner-spells cell
// (see ClassicRecommendedPanel). The old standalone Skill Priority + Skill Path
// module was removed in QA, so only the 3-ability priority order survives — now
// sourced from the compact "mini" feed (useClassicChampionSkillsMini). The
// resolution logic lives in resolveSkillPriorityMini; the icon in ClassicSkillIcon.

const SKILL_KEY_LABEL: Record<string, string> = { q: "Q", w: "W", e: "E", r: "R", p: "P" };

// Ability keys that can appear in a priority entry. The mini feed uses uppercase
// ("Q"); we lowercase to match SKILL_KEY_LABEL and the icon's skillKey prop.
const VALID_SKILL_PRIORITY_KEYS = new Set(["q", "w", "e", "r"]);

// Resolve the skill-priority order from the mini feed — a flat array of ability
// keys, e.g. ["Q","W","E"]. Defensive against a malformed payload: a non-array,
// non-string entries, or unknown keys are dropped. Returns the lowercased keys,
// or an empty array (→ blank placeholder slots) when there's no usable data.
function resolveSkillPriorityMini(mini: ClassicChampionSkillsMini | undefined): string[] {
  return asArray(mini)
    .map((k) => asText(k).toLowerCase())
    .filter((k) => VALID_SKILL_PRIORITY_KEYS.has(k));
}

// A single champion-skill icon (P/Q/W/E/R) with the shared hover tooltip (name,
// cooldown, description). Used by the Skill Priority row in the Recommended panel.
function ClassicSkillIcon(props: {
  championKey?: string;
  champion?: ClassicChampionData;
  skillKey: "p" | "q" | "w" | "e" | "r";
  className: string;
}) {
  const { championKey, champion, skillKey, className } = props;
  const isPassive = skillKey === "p";
  const spell = asArray(champion?.spells).find((s) => s.spellKey === skillKey);
  const tipName = isPassive ? champion?.passive?.name : spell?.name;
  return (
    <TooltipContainer
      disableTooltip={!champion}
      tooltipInfo={
        <SkillTooltipContent
          name={tipName || SKILL_KEY_LABEL[skillKey]}
          cooldown={formatCooldown(spell?.cooldownCoefficients)}
          description={isPassive ? champion?.passive?.description : spell?.dynamicDescription}
          isPassive={isPassive}
        />
      }
    >
      <div className="relative flex-none">
        {championKey && (
          <img
            src={getClassicChampionSkillImgUrl(championKey, skillKey)}
            alt={tipName || SKILL_KEY_LABEL[skillKey]}
            className={className}
            style={{ width: 36, height: 36 }}
          />
        )}
        {/* Keybind badge — same overlay the champion header uses, so the Skill
            Priority icons read identically to the top module. */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex items-center justify-center w-[12px] h-[12px] rounded-full border-[2px] border-[#17172e] bg-[#383864] text-[9px] font-bold leading-none">
          {SKILL_KEY_LABEL[skillKey]}
        </div>
      </div>
    </TooltipContainer>
  );
}

// ─── Items ────────────────────────────────────────────────────────────────
//
// Colors for the item-description markup (attention = stat numbers, jadeUnique =
// gold labels, plus the shared value-color tags). <mainText>/<stats> are pure
// wrappers (no style) so their text renders default. Non-color tags carry extra
// style (bold/italic/size).
const ITEM_TAG_STYLE: Record<string, CSSProperties> = {
  attention: { color: "#ffffff", fontWeight: 700 },
  jadeUnique: { color: "#e0b354", fontWeight: 700 },
  keyword: { color: "#f0e6d2" },
  keywordMajor: { color: "#f0e6d2", fontWeight: 700 },
  gold: { color: "#e0b354" },
  danger: { color: "#e0524f" },
  jadeLimit: { color: "#e0524f" },
  flavorText: { color: "#8b8ba7", fontStyle: "italic" },
  jadeRules: { color: "#8b8ba7" },
  rules: { color: "#8b8ba7" },
  magicDamage: { color: "#31c8e6" },
  physicalDamage: { color: "#ff9b6b" },
  trueDamage: { color: "#e0c56e" },
  scaleAP: { color: "#31c8e6" },
  scaleAD: { color: "#ff9b6b" },
  scaleHealth: { color: "#8fce5b" },
  scaleMana: { color: "#5db6ff" },
  scaleArmor: { color: "#f0b072" },
  scaleMR: { color: "#8fd6ff" },
  healing: { color: "#4bd15a" },
  lifeSteal: { color: "#4bd15a" },
  spellVamp: { color: "#4bd15a" },
  shield: { color: "#c9c9d6" },
  speed: { color: "#e0a458" },
  status: { color: "#ffffff", fontWeight: 700 },
  spellName: { color: "#d5a353" },
  armorPen: { color: "#ff9b6b" },
  magicPen: { color: "#31c8e6" },
  attackSpeed: { color: "#e0a458" },
  scaleCrit: { color: "#ff9b6b" },
  abilityHaste: { color: "#31c8e6" },
};

// Render an item's HTML description (custom tags + <br>) into styled nodes,
// using the top-of-stack tag style for each text run.
function renderItemDescription(html: string | undefined): ReactNode {
  if (typeof html !== "string" || !html) return null;
  const tokens = html.split(/(<br\s*\/?>|<\/?[a-zA-Z][a-zA-Z0-9]*>)/g);
  const styleStack: Array<CSSProperties | undefined> = [];
  const nodes: ReactNode[] = [];
  let key = 0;
  for (const tok of tokens) {
    if (!tok) continue;
    if (/^<br\s*\/?>$/.test(tok)) {
      nodes.push(<br key={key++} />);
      continue;
    }
    const open = tok.match(/^<([a-zA-Z][a-zA-Z0-9]*)>$/);
    if (open) {
      styleStack.push(ITEM_TAG_STYLE[open[1]]);
      continue;
    }
    if (/^<\/[a-zA-Z][a-zA-Z0-9]*>$/.test(tok)) {
      styleStack.pop();
      continue;
    }
    const style = styleStack[styleStack.length - 1];
    nodes.push(
      style ? (
        <span key={key++} style={style}>
          {tok}
        </span>
      ) : (
        <Fragment key={key++}>{tok}</Fragment>
      ),
    );
  }
  return <>{nodes}</>;
}

// Item detail body (name + price header, divider, styled description). Used both
// for the hover tooltip and the Items Summary cards (which show their own icon
// alongside). No icon here, matching the rest of the site's tooltips.
function ClassicItemDetails(props: { item: ClassicItem }) {
  const { item } = props;
  return (
    <div className="max-w-[320px] text-[13px] leading-[18px]">
      <div className="flex items-start justify-between gap-[12px]">
        <span className="min-w-0 font-bold text-white">{item.name}</span>
        {item.priceTotal > 0 && <span className="flex-none font-bold text-[#e0b354]">{item.priceTotal}g</span>}
      </div>
      <div className="my-[8px] border-t border-purple-100" />
      <div className="text-lavender-50">{renderItemDescription(item.description)}</div>
    </div>
  );
}

// A build item: icon (+ quantity badge for stacked consumables) with a hover
// tooltip once the full item manifest resolves. Bare icon until then.
function ClassicItemIcon(props: { itemId: number; item?: ClassicItem; name?: string; quantity?: number }) {
  const { itemId, item, name, quantity } = props;
  const img = (
    <img
      src={getClassicItemIconUrl(itemId)}
      alt={item?.name || name || `Item ${itemId}`}
      className="flex-none rounded-[3px]"
      style={{ width: 36, height: 36 }}
    />
  );
  const content =
    quantity && quantity > 1 ? (
      // Quantity badge — same overlay treatment as the skill keybind, pinned to
      // the icon's bottom-right corner.
      <div className="relative flex-none">
        {img}
        <div className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 flex items-center justify-center min-w-[16px] h-[16px] px-[3px] rounded-full border-[2px] border-[#17172e] bg-[#383864] text-white text-[10px] font-bold leading-none">
          {quantity}
        </div>
      </div>
    ) : (
      img
    );
  if (!item) return content;
  return <TooltipContainer tooltipInfo={<ClassicItemDetails item={item} />}>{content}</TooltipContainer>;
}

function ClassicItems(props: {
  itemBuild?: ClassicItemBuildFile;
  itemsById: Record<number, ClassicItem>;
  itemsMini?: Record<string, string>;
}) {
  const { itemBuild, itemsById, itemsMini } = props;
  const starter = asArray(itemBuild?.items?.starter);
  const endgame = asArray(itemBuild?.items?.endgame);

  // Unique starter ids (first 2); duplicate ids count as a stacked consumable.
  const starterCounts = new Map<number, number>();
  starter.forEach((id) => starterCounts.set(id, (starterCounts.get(id) || 0) + 1));
  const starterUnique = [...starterCounts.keys()].slice(0, 2);

  // Endgame (≤6) mapped onto the legacy option slots: 2 / 3 / 1.
  const optionSections: Array<{ key: string; label: string; blurb?: string; ids: number[] }> = [
    { key: "item-options-1", label: "Fourth Item Options", blurb: "Options after core build", ids: endgame.slice(0, 2) },
    { key: "item-options-2", label: "Fifth Item Options", ids: endgame.slice(2, 5) },
    { key: "item-options-3", label: "Sixth Item Options", ids: endgame.slice(5, 6) },
  ];

  const emptyItem = <div className="w-[36px] h-[36px] rounded-[3px] bg-[#25254b]/50" />;
  const renderIcon = (id: number, quantity?: number) => (
    <ClassicItemIcon itemId={id} item={itemsById[id]} name={itemsMini?.[String(id)]} quantity={quantity} />
  );

  return (
    // !mt-0 neutralizes the legacy `.recommended-build_items { margin-top }`.
    <div className="content-section content-section_no-padding recommended-build_items !mt-0">
      <div className="content-section_content starting-items">
        <div className="content-section_header whitespace-nowrap">Starting Items</div>
        <div>
          <div className="flex items-center justify-center gap-[8px]">
            {starterUnique.length > 0 ? (
              starterUnique.map((id) => <Fragment key={id}>{renderIcon(id, starterCounts.get(id))}</Fragment>)
            ) : (
              <>
                {emptyItem}
                {emptyItem}
              </>
            )}
          </div>
          <div className="item-stats" />
        </div>
        <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
          <span className="content-section_blurb whitespace text-center w-full">Best for most matchups</span>
        </MediaQuery>
      </div>

      {/* Core Items stays a placeholder until we wire the core-build feed. */}
      <div className="content-section_content core-items">
        <div>
          <div className="content-section_header whitespace-nowrap">Core Items</div>
          <div className="flex items-center justify-center gap-[8px]">
            {emptyItem}
            {emptyItem}
            {emptyItem}
          </div>
          <div className="item-stats" />
        </div>
        <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
          <span className="content-section_blurb whitespace-nowrap">Build this every game</span>
        </MediaQuery>
      </div>

      {optionSections.map(({ key, label, blurb, ids }) => (
        <div key={key} className={`content-section_content item-options ${key}`}>
          <div className="content-section_header whitespace-nowrap">{label}</div>
          <div className="item-option-list">
            {ids.map((id, i) => (
              <div key={`${id}-${i}`} className="item-option flex items-center gap-[8px]">
                {renderIcon(id)}
                <span className="flex-1 text-left text-white text-[10px] leading-tight">{itemsMini?.[String(id)]}</span>
              </div>
            ))}
          </div>
          {blurb && (
            <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
              <span className="content-section_blurb whitespace-nowrap">{blurb}</span>
            </MediaQuery>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Items summary (icon + tooltip card, 2×3, after Mastery Summary) ──────────
function ClassicItemsSummary(props: {
  endgame: number[];
  itemsById: Record<number, ClassicItem>;
  itemsMini?: Record<string, string>;
}) {
  const { endgame, itemsById, itemsMini } = props;
  const items = asArray(endgame).slice(0, 6);
  if (items.length === 0) return null;

  return (
    <div className="content-section">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Items Summary</span>
        <span className="content-section_blurb">Endgame item details</span>
      </div>
      <div className="grid grid-cols-2 gap-[12px] max-md:grid-cols-1">
        {items.map((id, i) => {
          const item = itemsById[id];
          return (
            <div key={`${id}-${i}`} className="flex items-start gap-[14px] rounded-[3px] border border-purple-100 p-[14px]">
              <img
                src={getClassicItemIconUrl(id)}
                alt={item?.name || itemsMini?.[String(id)] || `Item ${id}`}
                className="flex-none w-[44px] h-[44px] rounded-[3px]"
              />
              <div className="min-w-0 flex-1">
                {item ? (
                  <ClassicItemDetails item={item} />
                ) : (
                  <div className="text-[14px] font-bold text-white">{itemsMini?.[String(id)] || `Item ${id}`}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Champion Info (replaces the Role Quest slot) ─────────────────────────────

function ClassicChampionInfo(props: { champion?: ClassicChampionData }) {
  const { champion } = props;
  if (!champion) return null;

  const facts = asText(champion.shortBio)
    .split(/<br\s*\/?>/g)
    .map((line) => line.replace(/^\s*-\s*/, "").trim())
    .filter(Boolean);

  const roleTags = asArray(champion.roles).map((r) => asText(r).toUpperCase());
  const kindTags = [champion.championTagInfo?.championTagPrimary, champion.championTagInfo?.championTagSecondary]
    .filter(Boolean)
    .map((t) => asText(t).toUpperCase());

  const bars: Array<{ label: string; value: number }> = [
    { label: "Damage", value: champion.playstyleInfo?.damage ?? 0 },
    { label: "Durability", value: champion.playstyleInfo?.durability ?? 0 },
    { label: "Crowd Control", value: champion.playstyleInfo?.crowdControl ?? 0 },
    { label: "Mobility", value: champion.playstyleInfo?.mobility ?? 0 },
    { label: "Utility", value: champion.playstyleInfo?.utility ?? 0 },
  ];

  const Pill = ({ children }: { children: ReactNode }) => (
    <span className="flex items-center h-[26px] px-[10px] rounded-[3px] border border-accent-blue-400 text-accent-blue-400 text-[12px] font-bold">
      {children}
    </span>
  );

  return (
    <div className="content-section">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Champion Info</span>
        <span className="content-section_blurb">Overview</span>
      </div>

      <div className="text-[16px] font-bold text-white">{champion.title}</div>
      {facts.length > 0 && (
        <div className="mt-[10px] flex flex-col gap-[4px] text-[14px] text-lavender-50">
          {facts.map((fact, i) => (
            <div key={i}>- {fact}</div>
          ))}
        </div>
      )}

      <div className="mt-[16px] flex items-center gap-[8px] flex-wrap">
        {roleTags.map((tag) => (
          <Pill key={`role-${tag}`}>{tag}</Pill>
        ))}
        {kindTags.length > 0 && roleTags.length > 0 && <div className="w-[1px] h-[20px] bg-purple-100" />}
        {kindTags.map((tag) => (
          <Pill key={`kind-${tag}`}>{tag}</Pill>
        ))}
      </div>

      <div className="mt-[16px] flex flex-col gap-[10px] max-w-[360px]">
        {bars.map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between">
            <div className="text-[13px] font-bold text-lavender-50">{label}</div>
            {/* 3 gold pips filled to the playstyleInfo value (0–3). */}
            <div className="flex items-center gap-[6px]">
              {[0, 1, 2].map((seg) => (
                <div
                  key={seg}
                  className="w-[26px] h-[14px] -skew-x-[16deg] rounded-[2px]"
                  style={{ backgroundColor: seg < value ? "#c8aa6e" : "#26264b" }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Skills (icon + name + description per spell card) ────────────────────────

// Spell cards showing the ability icon, name and plain description. The full
// tooltip (cooldown + resolved dynamic description) is shown on icon hover,
// matching the header's skill-icon preview. Laid out 2-up: Q/W, E/R, then P.
function ClassicSkillsDetail(props: { championKey?: string; champion?: ClassicChampionData }) {
  const { championKey, champion } = props;

  const entries: Array<{
    key: "q" | "w" | "e" | "r" | "p";
    name?: string;
    description?: string;
    dynamic?: string;
    cooldown?: string | null;
    isPassive: boolean;
  }> = useMemo(() => {
    if (!champion) return [];
    const spellEntries = (["q", "w", "e", "r"] as const).map((key) => {
      const spell = asArray(champion.spells).find((s) => s.spellKey === key);
      return {
        key,
        name: spell?.name,
        description: spell?.description,
        dynamic: spell?.dynamicDescription,
        cooldown: formatCooldown(spell?.cooldownCoefficients),
        isPassive: false,
      };
    });
    return [
      ...spellEntries,
      {
        key: "p" as const,
        name: champion.passive?.name,
        description: champion.passive?.description,
        dynamic: champion.passive?.description,
        cooldown: null,
        isPassive: true,
      },
    ];
  }, [champion]);

  return (
    <div className="content-section">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Skills</span>
        <span className="content-section_blurb">Classic champion abilities</span>
      </div>

      <div className="grid grid-cols-3 gap-[12px] max-md:grid-cols-2 max-sm:grid-cols-1">
        {entries.map(({ key, name, description, dynamic, cooldown, isPassive }) => (
          <div key={key} className="rounded-[3px] bg-[#11112a] p-[16px] font-['Inter']">
            <div className="flex items-center gap-[12px]">
              {/* Icon carries the full tooltip on hover (cooldown + resolved
                  dynamic description), like the header's skill-icon preview. */}
              <TooltipContainer
                disableTooltip={!champion}
                tooltipInfo={
                  <SkillTooltipContent
                    name={name || SKILL_KEY_LABEL[key]}
                    cooldown={cooldown}
                    description={dynamic}
                    isPassive={isPassive}
                  />
                }
              >
                <div className="relative flex-none">
                  {championKey && (
                    <img
                      src={getClassicChampionSkillImgUrl(championKey, key)}
                      alt={name || SKILL_KEY_LABEL[key]}
                      loading="lazy"
                      className="w-[36px] h-[36px] rounded-[3px]"
                    />
                  )}
                  {/* Keybind badge — same overlay the champion header and Skill
                      Priority row use (bottom-center, round), so every skill
                      icon on the page reads identically. */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex items-center justify-center w-[12px] h-[12px] rounded-full border-[2px] border-[#17172e] bg-[#383864] text-[9px] font-bold leading-none">
                    {SKILL_KEY_LABEL[key]}
                  </div>
                </div>
              </TooltipContainer>
              {/* Title + description sized to match the Rune Summary module
                  (category name 14px bold; effect/stat text 11px/15px) so this
                  text-heavy card doesn't stand out from the rest of the page. */}
              <div className="text-[14px] font-bold text-white">{name}</div>
            </div>
            <div className="mt-[18px] text-[11px] leading-[15px] text-lavender-50">{description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Rune summary (compact selected-rune cards, grouped by rune type) ─────────

// Compact take on the runes editor (like the mastery under-tree summaries):
// one full-width panel per rune type, each with a wrapping grid of "Nx · icon ·
// title · effect" cards. All info lives on the card, so no hover tooltips.
function ClassicRuneSummary(props: {
  runeBuild?: ClassicRuneBuildFile;
  perksById: Record<number, ClassicPerk>;
  name: string;
  roleLabel: string;
}) {
  const { runeBuild, perksById, name, roleLabel } = props;

  // Always render the module — one panel per rune category — even when the
  // champion has no rune build yet (missing/404). Categories with no runes show
  // just their label + accent, so the module stays on the page rather than
  // disappearing entirely. No filtering by entries.length and no early return.
  const groups = RUNE_CATEGORIES.map((cat) => ({ ...cat, entries: asArray(runeBuild?.runes?.[cat.key]) }));

  return (
    <div className="content-section">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Rune Summary</span>
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <span className="content-section_blurb">{`These are the best runes for ${name} ${roleLabel}`}</span>
        </MediaQuery>
      </div>

      <div className="flex flex-col gap-[12px]">
        {groups.map((group) => (
          <div
            key={group.key}
            // font-['Inter'] so this text matches the neighboring redesigned
            // modules. Without it the text falls back to Tailwind preflight's
            // system-ui stack (Segoe UI on Windows), which is wider than Inter
            // and reads as "stretched" next to the compact panel / masteries.
            className="relative overflow-hidden rounded-[3px] bg-[#11112a] py-[14px] pl-[18px] pr-[16px] font-['Inter']"
          >
            <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: group.color }} />
            <div className="mb-[12px] text-[14px] font-bold text-white">{group.label}</div>
            <div
              className="grid gap-x-[16px] gap-y-[12px]"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
            >
              {group.entries.map((entry, i) => {
                const [perkId, count] = Array.isArray(entry) ? entry : [0, 0];
                const perk = perksById[perkId];
                return (
                  <div key={`${perkId}-${i}`} className="flex items-center gap-[10px]">
                    <div className="w-[26px] flex-none text-center text-[12px] font-bold text-white">{count}x</div>
                    {perk?.iconPath ? (
                      <img
                        src={getClassicRuneIconUrl(perk.iconPath)}
                        alt={perk.title}
                        loading="lazy"
                        className="flex-none w-[36px] h-[36px]"
                      />
                    ) : (
                      <div className="flex-none w-[36px] h-[36px] rounded-full bg-[#25254b]/50" />
                    )}
                    <div className="min-w-0">
                      <div className="text-[12px] font-bold text-white">{perk?.title ?? `Rune ${perkId}`}</div>
                      <div className="text-[11px] leading-[15px] text-lavender-50">{perk?.tooltip}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Mastery summary (compact selected-mastery cards + tooltips) ──────────────

// Highlight the value at the current rank within a "a/b/c" run in a mastery
// description (e.g. "+1.5/3/4.5/6 Health per level" at rank 4 → bold "6").
function renderMasteryDescription(description: string, rank: number): ReactNode {
  if (typeof description !== "string") return null;
  const re = /(\d+(?:\.\d+)?%?(?:\s*\/\s*\d+(?:\.\d+)?%?)+)/;
  const m = description.match(re);
  if (!m || m.index === undefined) return description;

  const group = m[0];
  const parts = group.split("/").map((s) => s.trim());
  // rank 0 (unallocated node in a tooltip) → highlight nothing.
  const idx = rank > 0 ? Math.min(rank - 1, parts.length - 1) : -1;
  const before = description.slice(0, m.index);
  const after = description.slice(m.index + group.length);

  return (
    <>
      {before}
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 && "/"}
          <span className={i === idx ? "text-accent-gold-400 font-bold" : undefined}>{p}</span>
        </Fragment>
      ))}
      {after}
    </>
  );
}

function ClassicMasterySummary(props: {
  masteryDisplay?: { trees: Array<{ name: string; type: string; rows: Array<{ masteries: Array<ClassicMasteryNode | null> }> }> };
  selected: Record<number, number>;
}) {
  const { masteryDisplay, selected } = props;
  const trees = asArray(masteryDisplay?.trees);

  const treeColor: Record<string, string> = {
    offense: "text-accent-red-400",
    defense: "text-accent-blue-400",
    utility: "text-accent-forest-green-400",
  };

  // Preserve grid order within each tree (top rows first, left→right).
  const grouped = useMemo(() => {
    return trees.map((tree) => {
      const nodes: ClassicMasteryNode[] = [];
      asArray(tree.rows).forEach((row) =>
        asArray(row.masteries).forEach((node) => node && selected[node.id] && nodes.push(node)),
      );
      return { tree, nodes };
    });
  }, [trees, selected]);

  // Nothing allocated (e.g. the champion has no curated tree yet) → no summary.
  if (trees.length === 0 || grouped.every((g) => g.nodes.length === 0)) return null;

  return (
    <div className="content-section">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Mastery Summary</span>
        <span className="content-section_blurb">Selected masteries</span>
      </div>

      <div className="flex flex-col gap-[18px]">
        {grouped.map(({ tree, nodes }) =>
          nodes.length === 0 ? null : (
            <div key={tree.name} className="rounded-[3px] bg-[#11112a] p-[14px]">
              <div className={classNames("mb-[10px] text-[14px] font-bold", treeColor[tree.type])}>{tree.name}</div>
              <div className="grid grid-cols-2 gap-[10px] max-sm:grid-cols-1">
                {nodes.map((node) => {
                  const rank = selected[node.id] || 0;
                  return (
                    <div key={node.id} className="flex items-start gap-[10px] rounded-[3px] bg-purple-200 p-[10px]">
                      <img
                        src={getClassicMasteryIconUrl(node.id, true)}
                        alt={node.name}
                        className="flex-none w-[36px] h-[36px] rounded-[3px]"
                      />
                      <div className="min-w-0">
                        <div className="text-[13px] font-bold text-accent-blue-400">{node.name}</div>
                        <div className="text-[11px] text-accent-gray-100">
                          Rank {rank} / {node.maxRank}
                        </div>
                        <div className="mt-[4px] text-[12px] leading-[16px] text-white">
                          {renderMasteryDescription(node.description, rank)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
}
